import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Label, Select, Spinner } from 'flowbite-react';
import { toast } from 'react-toastify';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import ProtectedRoute from '../../components/ProtectedRoute.tsx';
import { toastError } from '../../components/Toast.tsx';
import { useAppSelector } from '../../lib/hooks.ts';
import { useDeleteBuildingMutation, useGetAllBuildingsQuery, useSetDefaultBuildingMutation } from '../../lib/model/kantoordagApi.ts';
import { userHasRole } from '../../lib/user-has-role.tsx';
import { ROOM__DEFAULT_ROOMS } from '../Constants.tsx';
import BuildingConfigForm from './BuildingConfigForm.tsx';
import CreateBuildingDialog from './CreateBuildingDialog.tsx';
import RoomList from './RoomList.tsx';

export default function BuildingAdmin() {
    return (
        <ProtectedRoute authenticated roles={['ADMIN']}>
            <BuildingAdminInternal/>
        </ProtectedRoute>
    )
}

function BuildingAdminInternal() {
    const { t } = useTranslation('building-admin');
    const user = useAppSelector(state => state.user.user);
    // Deleting a building doesn't cascade-clean derived data and can't be undone (Roles.kt) --
    // ADMIN alone is refused by the backend, so hide the trigger here too rather than let a
    // plain admin hit a 403 after confirming.
    const canDeleteBuilding = userHasRole(user, ['SUPER_ADMIN']);
    const { data: buildings, isLoading, isError, refetch } = useGetAllBuildingsQuery();
    const [selectedBuildingName, setSelectedBuildingName] = useState<string | undefined>(undefined);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [setDefaultBuilding, setDefaultBuildingResult] = useSetDefaultBuildingMutation();
    const [deleteBuilding, deleteBuildingResult] = useDeleteBuildingMutation();

    const selectedBuilding = buildings?.find(building => building.name === selectedBuildingName);
    // A building with hasRooms=false can still carry pre-existing physical rooms (from before the
    // flag was turned off, or from before this feature existed) -- spec.md's resolved edge case:
    // "the screen should still let an administrator see and clean these up, not hide them just
    // because the feature flag says rooms don't apply."
    const hasPhysicalRooms = selectedBuilding != null
        && selectedBuilding.rooms.some(room => !ROOM__DEFAULT_ROOMS.includes(room.name));

    function makeDefault() {
        if (selectedBuilding == null) {
            return;
        }
        setDefaultBuilding({ building: selectedBuilding.name })
            .unwrap()
            .then(() => {
                toast.success(t('building_set_default'));
                refetch();
            })
            .catch(() => toastError({ api: setDefaultBuildingResult, message: t('building_set_default_failed') }));
    }

    function confirmDeleteBuilding() {
        if (selectedBuilding == null) {
            return;
        }
        setShowDeleteConfirm(false);
        deleteBuilding({ building: selectedBuilding.name })
            .unwrap()
            .then(() => {
                toast.success(t('building_deleted'));
                setSelectedBuildingName(undefined);
                refetch();
            })
            .catch((error) => {
                const message = error?.status === 409 ? t('building_delete_default_failed') : t('building_delete_failed');
                toastError({ api: deleteBuildingResult, message });
            });
    }

    return (
        <Card className="mx-auto mt-4 max-w-4xl">
            <h1 className="text-xl font-semibold">{t('title')}</h1>

            {isLoading && <Spinner/>}
            {isError && <p className="text-red-600">{t('loading_failed')}</p>}

            {buildings != null && (
                <div className="flex items-end gap-4">
                    <div className="max-w-sm grow">
                        <Label htmlFor="building-select">{t('select_building')}</Label>
                        <Select
                            id="building-select"
                            value={selectedBuildingName ?? ''}
                            onChange={event => setSelectedBuildingName(event.target.value || undefined)}
                        >
                            <option value="">{t('select_building')}</option>
                            {buildings.map(building => (
                                <option key={building.name} value={building.name}>
                                    {building.name}{building.default ? ' *' : ''}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <Button color="secondary" onClick={() => setShowCreateDialog(true)}>
                        {t('create_building')}
                    </Button>
                </div>
            )}

            {selectedBuilding != null && (
                <div data-testid="building-admin-detail" className="mt-4 flex flex-col gap-6">
                    <div className="flex gap-2">
                        {!selectedBuilding.default && (
                            <Button id="building-set-default" onClick={makeDefault}>
                                {t('set_default')}
                            </Button>
                        )}
                        {canDeleteBuilding && (
                            <div className="flex flex-col gap-1">
                                <Button
                                    id="building-delete"
                                    color="failure"
                                    disabled={selectedBuilding.default}
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    {t('delete_building')}
                                </Button>
                                {selectedBuilding.default && (
                                    <p className="text-sm text-gray-500">{t('building_delete_default_disabled')}</p>
                                )}
                            </div>
                        )}
                    </div>
                    <BuildingConfigForm building={selectedBuilding} key={selectedBuilding.name}/>
                    {selectedBuilding.features.hasRooms || hasPhysicalRooms ? (
                        <RoomList building={selectedBuilding} onChanged={refetch}/>
                    ) : (
                        <p className="text-gray-500">{t('rooms_not_applicable')}</p>
                    )}
                </div>
            )}

            <CreateBuildingDialog
                show={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onCreated={buildingName => {
                    refetch();
                    setSelectedBuildingName(buildingName);
                }}
            />

            <ConfirmDialog
                message={t('confirm_delete_building', { building: selectedBuilding?.name })}
                yes={t('confirm_yes')}
                no={t('confirm_no')}
                show={showDeleteConfirm}
                onYes={confirmDeleteBuilding}
                onNo={() => setShowDeleteConfirm(false)}
            />
        </Card>
    )
}
